<div class="content-wrapper blog-form-page">
    <div class="content-body">
        <form [formGroup]="form" class="blog-form" novalidate>

            <!-- Header bar -->
            <section class="form-header d-flex align-items-center justify-content-between flex-wrap">
                <div>
                    <button type="button" class="btn btn-link p-0 text-muted" (click)="cancel()">
                        <i class="fa fa-arrow-left mr-1"></i> Back to posts
                    </button>
                    <h2 class="mt-1 mb-0">{{ isEdit ? 'Edit Post' : 'New Post' }}</h2>
                </div>
                <div class="header-actions">
                    <button type="button" class="btn btn-outline-secondary mr-1" (click)="openPreview()">
                        <i class="fa fa-eye mr-1"></i> Preview
                    </button>
                    <button type="button" class="btn btn-outline-primary mr-1" (click)="save(false)"
                            [disabled]="saving">
                        <i class="fa fa-save mr-1"></i> Save Draft
                    </button>
                    <button type="button" class="btn btn-primary" (click)="save(true)" [disabled]="saving">
                        <i class="fa fa-paper-plane mr-1"></i>
                        {{ form.value.status === 'PUBLISHED' ? 'Update' : 'Publish' }}
                    </button>
                </div>
            </section>

            <div class="row mt-3">
                <!-- Main column -->
                <div class="col-12 col-lg-8">
                    <div class="card form-card">
                        <div class="card-body">
                            <label class="field-label">Title</label>
                            <input type="text" class="form-control form-control-lg title-input"
                                   formControlName="title" placeholder="Give your post a great title...">
                            <small class="text-danger"
                                   *ngIf="form.get('title').touched && form.get('title').errors?.required">
                                Title is required
                            </small>

                            <label class="field-label mt-3">URL slug</label>
                            <div class="slug-input-wrap">
                                <span class="slug-prefix">/blog/</span>
                                <input id="blog-slug-input" type="text" class="form-control"
                                       formControlName="slug" placeholder="auto-generated-from-title">
                            </div>

                            <label class="field-label mt-3">Short intro</label>
                            <textarea class="form-control" rows="3" formControlName="intro"
                                      placeholder="A 1-2 sentence summary shown on cards and search results"
                                      maxlength="1000"></textarea>
                            <small class="text-muted">{{ form.value.intro?.length || 0 }} / 1000</small>
                        </div>
                    </div>

                    <!-- Cover -->
                    <div class="card form-card mt-3">
                        <div class="card-header">
                            <h5 class="mb-0">Cover Image</h5>
                            <small class="text-muted">Recommended 16:9 ratio · max 5 MB</small>
                        </div>
                        <div class="card-body">
                            <ng-container *ngIf="!showCropper">
                                <div class="cover-preview" *ngIf="previewCoverUrl; else coverDrop">
                                    <img [src]="previewCoverUrl" alt="Cover">
                                    <div class="cover-overlay">
                                        <label class="btn btn-light btn-sm mr-1" for="cover-input">
                                            <i class="fa fa-refresh mr-1"></i> Replace
                                        </label>
                                        <button type="button" class="btn btn-outline-light btn-sm"
                                                (click)="removeCover()">
                                            <i class="fa fa-trash mr-1"></i> Remove
                                        </button>
                                    </div>
                                </div>
                                <ng-template #coverDrop>
                                    <label for="cover-input" class="cover-drop">
                                        <i class="fa fa-cloud-upload"></i>
                                        <span>Click to upload a cover image</span>
                                        <small>PNG, JPG up to 5 MB</small>
                                    </label>
                                </ng-template>
                                <input id="cover-input" type="file" accept="image/*"
                                       (change)="onCoverPicked($event)" hidden>
                            </ng-container>

                            <div class="cover-cropper" *ngIf="showCropper">
                                <image-cropper
                                    [imageChangedEvent]="imageChangedEvent"
                                    [maintainAspectRatio]="true"
                                    [aspectRatio]="16/9"
                                    [resizeToWidth]="1600"
                                    format="png"
                                    (imageCropped)="onImageCropped($event)">
                                </image-cropper>
                                <div class="mt-2 text-right">
                                    <button type="button" class="btn btn-outline-secondary mr-1"
                                            (click)="cancelCrop()">Cancel
                                    </button>
                                    <button type="button" class="btn btn-primary" (click)="confirmCrop()">
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Tabs -->
                    <div class="card form-card mt-3">
                        <ul ngbNav #nav="ngbNav" [(activeId)]="activeTab" class="nav-tabs blog-tabs">
                            <li [ngbNavItem]="'content'">
                                <a ngbNavLink>Content</a>
                                <ng-template ngbNavContent>
                                    <label class="field-label">Body</label>
                                    <quill-editor formControlName="content"
                                                  [modules]="quillModules"
                                                  [styles]="{height: '420px'}"
                                                  placeholder="Write your story...">
                                    </quill-editor>
                                </ng-template>
                            </li>
                            <li [ngbNavItem]="'seo'">
                                <a ngbNavLink>SEO</a>
                                <ng-template ngbNavContent>
                                    <div class="row">
                                        <div class="col-12">
                                            <label class="field-label">Meta title</label>
                                            <input type="text" class="form-control"
                                                   formControlName="meta_title"
                                                   placeholder="Defaults to post title">
                                        </div>
                                        <div class="col-12 mt-2">
                                            <label class="field-label">Meta description</label>
                                            <textarea class="form-control" rows="3"
                                                      formControlName="meta_description"
                                                      placeholder="160 chars recommended for search engines"
                                                      maxlength="500"></textarea>
                                            <small class="text-muted">
                                                {{ form.value.meta_description?.length || 0 }} / 500
                                            </small>
                                        </div>
                                        <div class="col-12 mt-2">
                                            <label class="field-label">Keywords</label>
                                            <input type="text" class="form-control"
                                                   formControlName="meta_keywords"
                                                   placeholder="comma, separated, keywords">
                                        </div>
                                    </div>
                                </ng-template>
                            </li>
                        </ul>
                        <div [ngbNavOutlet]="nav" class="p-3"></div>
                    </div>
                </div>

                <!-- Side column -->
                <div class="col-12 col-lg-4">
                    <div class="card form-card sticky-side">
                        <div class="card-header">
                            <h5 class="mb-0">Publish</h5>
                        </div>
                        <div class="card-body">
                            <label class="field-label">Status</label>
                            <select class="form-control" formControlName="status">
                                <option value="DRAFT">Draft</option>
                                <option value="PUBLISHED">Published</option>
                            </select>

                            <label class="field-label mt-3">Publish date</label>
                            <input type="datetime-local" class="form-control"
                                   formControlName="published_at">

                            <div class="form-check mt-3">
                                <input id="featured-flag" type="checkbox" class="form-check-input"
                                       formControlName="is_featured">
                                <label for="featured-flag" class="form-check-label">
                                    Featured post
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="card form-card mt-3">
                        <div class="card-header">
                            <h5 class="mb-0">Category</h5>
                        </div>
                        <div class="card-body">
                            <select class="form-control" formControlName="category_id">
                                <option [ngValue]="null">— None —</option>
                                <option *ngFor="let c of categories" [ngValue]="c.id">{{ c.name }}</option>
                            </select>
                            <a class="d-inline-block mt-2 small" routerLink="/blogs/categories">
                                <i class="fa fa-plus mr-1"></i> Manage categories
                            </a>
                        </div>
                    </div>

                    <div class="card form-card mt-3">
                        <div class="card-header">
                            <h5 class="mb-0">Tags</h5>
                        </div>
                        <div class="card-body">
                            <div class="tag-pool">
                                <span class="tag-pill" *ngFor="let t of tags"
                                      [class.selected]="isTagSelected(t)"
                                      (click)="toggleTag(t)">
                                    #{{ t.name }}
                                </span>
                            </div>
                            <div class="input-group mt-2">
                                <input #newTag type="text" class="form-control form-control-sm"
                                       placeholder="Add new tag">
                                <div class="input-group-append">
                                    <button type="button" class="btn btn-sm btn-outline-primary"
                                            (click)="addNewTag(newTag.value); newTag.value=''">
                                        <i class="fa fa-plus"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </form>

        <!-- Preview Modal -->
        <div class="preview-modal" *ngIf="showPreview">
            <div class="preview-backdrop" (click)="closePreview()"></div>
            <div class="preview-dialog">
                <div class="preview-header">
                    <h5>Preview</h5>
                    <button class="btn btn-sm btn-light" (click)="closePreview()">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="preview-body">
                    <article class="preview-article">
                        <img *ngIf="previewCoverUrl" [src]="previewCoverUrl" class="preview-cover" alt="cover">
                        <h1 class="preview-title">{{ form.value.title || 'Untitled' }}</h1>
                        <p class="preview-intro" *ngIf="form.value.intro">{{ form.value.intro }}</p>
                        <hr>
                        <div class="preview-content" [innerHTML]="form.value.content"></div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</div>
